/**
 * 관세환율 (?page=exchange) — 관세청 주간 고시환율(UNIPASS API012 직결, 공개).
 * 수입/수출 토글 · 통화/국가(한글) 검색 · 적용 주 표시. TRADEVERSE 관세환율관리 대응.
 */
import React, { useState, useEffect, useMemo } from "react";
import ApiPortalLayout from "./ApiPortalLayout";
import COUNTRY_KO from "./countryKo";

const S = {
  page: { maxWidth: 880, color: "#1f2937",
    fontFamily: "'Inter','Pretendard',sans-serif", lineHeight: 1.65 },
  h1: { fontSize: 30, fontWeight: 800, marginBottom: 6 },
  p: { fontSize: 14, color: "#4b5563", margin: "6px 0" },
  card: { border: "1px solid #e5e7eb", borderRadius: 12, padding: "18px 20px", marginTop: 16 },
  input: { height: 44, padding: "0 12px", fontSize: 14, borderRadius: 8,
    border: "1px solid #d1d5db", outline: "none", background: "#fff",
    fontFamily: "inherit", boxSizing: "border-box" },
  th: { textAlign: "left", padding: "8px 12px", whiteSpace: "nowrap", fontSize: 12.5,
    fontWeight: 700, color: "#374151", position: "sticky", top: 0, zIndex: 2,
    background: "#f8fafc", boxShadow: "inset 0 -2px 0 #e5e7eb" },
  td: { padding: "7px 12px", borderBottom: "1px solid #f3f4f6", fontSize: 13.5,
    whiteSpace: "nowrap", background: "#fff" },
  note: { background: "#f8fafc", borderLeft: "3px solid #94a3b8", padding: "10px 14px",
    fontSize: 12.5, color: "#475569", borderRadius: 4, margin: "14px 0 0" },
};
const fmtDt = (d) => (d && d.length === 8 ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}` : d || "");

export default function ExchangeRatePage({ onNavigate, embedded = false }) {
  const [mode, setMode] = useState("customs"); // customs=관세청 고시 | bank=은행 고시
  const [imexTp, setImexTp] = useState("2"); // 2=수입, 1=수출 (관세청 모드 전용)
  const [data, setData] = useState(null);
  const [bank, setBank] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    setErr(null);
    if (mode === "customs") {
      setData(null);
      fetch(`/api/tools/exchange-rate?imexTp=${imexTp}`)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status))))
        .then((d) => alive && setData(d))
        .catch(() => alive && setErr("환율을 불러오지 못했습니다 — 잠시 후 다시 시도해 주세요."));
    } else if (!bank) {
      fetch("/api/tools/exchange-rate/bank")
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status))))
        .then((d) => alive && setBank(d))
        .catch(() => alive && setErr("은행 환율을 불러오지 못했습니다 — 잠시 후 다시 시도해 주세요."));
    }
    return () => { alive = false; };
  }, [mode, imexTp]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    const all = data?.rows || [];
    const t = q.trim().toUpperCase();
    if (!t) return all;
    return all.filter(
      (r) =>
        r.curr_sgn.includes(t) ||
        r.cnty_sgn.includes(t) ||
        (r.mtry_ut_nm || "").toUpperCase().includes(t) ||
        (COUNTRY_KO[r.cnty_sgn] || "").includes(q.trim()),
    );
  }, [data, q]);

  const bankRows = useMemo(() => {
    const all = bank?.rows || [];
    const t = q.trim().toUpperCase();
    if (!t) return all;
    return all.filter(
      (r) => r.code.includes(t) || (r.name || "").toUpperCase().includes(t) ||
        (r.name || "").includes(q.trim()),
    );
  }, [bank, q]);

  return (
    <ApiPortalLayout active="exchange" onNavigate={onNavigate} embedded={embedded}>
      <div style={S.page}>
        <h1 style={S.h1}>환율</h1>
        <p style={S.p}>
          <b>관세청 고시환율(과세환율)</b>은 수입 신고 시 과세가격 환산에,{" "}
          <b>은행 고시환율(매매기준율·전신환)</b>은 수출대금 정산·송금 실무에 사용됩니다 —
          두 환율을 한 화면에서 확인하세요.
        </p>

        {/* 소스 선택 — 관세청(주간) | 은행(당일 고시) */}
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          {[["customs", "관세청 고시환율 (주간)"], ["bank", "은행 고시환율 (매매기준율)"]].map(
            ([v, label]) => (
              <button
                key={v}
                onClick={() => { setMode(v); setQ(""); }}
                style={{
                  border: mode === v ? "1.5px solid #111827" : "1px solid #d1d5db",
                  borderRadius: 10, padding: "10px 18px", fontSize: 14,
                  fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  background: mode === v ? "#111827" : "#fff",
                  color: mode === v ? "#fff" : "#374151",
                }}
              >
                {label}
              </button>
            ),
          )}
        </div>

        <div style={S.card}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            {mode === "customs" && (
              <div
                style={{
                  display: "inline-flex", background: "#f1f3f8", borderRadius: 999, padding: 4,
                }}
              >
                {[["2", "수입"], ["1", "수출"]].map(([v, label]) => (
                  <button
                    key={v}
                    onClick={() => setImexTp(v)}
                    style={{
                      border: "none", borderRadius: 999, padding: "8px 22px",
                      fontSize: 13.5, fontWeight: 700, cursor: "pointer",
                      fontFamily: "inherit",
                      background: imexTp === v ? "#111827" : "transparent",
                      color: imexTp === v ? "#fff" : "#6b7280",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="통화·국가 검색 (예: 미국 / USD / JP)"
              style={{ ...S.input, flex: 1, minWidth: 220 }}
            />
            {mode === "customs" && data && (
              <span style={{ fontSize: 13, color: "#6b7280", whiteSpace: "nowrap" }}>
                적용 개시 <b>{fmtDt(data.week_start)}</b> · {data.count}개 통화
              </span>
            )}
            {mode === "bank" && bank && (
              <span style={{ fontSize: 13, color: "#6b7280", whiteSpace: "nowrap" }}>
                {bank.source_label} · {bank.count}개 통화
              </span>
            )}
          </div>

          <div
            style={{
              marginTop: 14, maxHeight: 560, overflow: "auto",
              border: "1px solid #e5e7eb", borderRadius: 8,
            }}
          >
            {err ? (
              <div style={{ padding: 16, fontSize: 13, color: "#b91c1c" }}>{err}</div>
            ) : mode === "customs" ? (
              !data ? (
                <div style={{ padding: 16, fontSize: 13, color: "#6b7280" }}>불러오는 중…</div>
              ) : (
                <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}>
                  <thead>
                    <tr>
                      <th style={S.th}>통화</th>
                      <th style={S.th}>국가</th>
                      <th style={S.th}>화폐단위</th>
                      <th style={{ ...S.th, textAlign: "right" }}>환율(원)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={`${r.curr_sgn}-${i}`}>
                        <td style={{ ...S.td, fontWeight: 700 }}>{r.curr_sgn}</td>
                        <td style={S.td}>
                          {COUNTRY_KO[r.cnty_sgn] || r.cnty_sgn}
                          <span style={{ color: "#9ca3af", fontSize: 12 }}> ({r.cnty_sgn})</span>
                        </td>
                        <td style={{ ...S.td, color: "#6b7280" }}>{r.mtry_ut_nm}</td>
                        <td
                          style={{
                            ...S.td, textAlign: "right", fontWeight: 700,
                            fontFamily: "ui-monospace,monospace",
                          }}
                        >
                          {Number(r.fxrt).toLocaleString("ko-KR", { maximumFractionDigits: 4 })}
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr><td style={S.td} colSpan={4}>검색 결과가 없습니다.</td></tr>
                    )}
                  </tbody>
                </table>
              )
            ) : !bank ? (
              <div style={{ padding: 16, fontSize: 13, color: "#6b7280" }}>불러오는 중…</div>
            ) : (
              <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}>
                <thead>
                  <tr>
                    <th style={S.th}>통화명</th>
                    <th style={{ ...S.th, textAlign: "right" }}>매매기준율</th>
                    <th style={{ ...S.th, textAlign: "right" }}>송금 보내실 때</th>
                    <th style={{ ...S.th, textAlign: "right" }}>송금 받으실 때</th>
                    <th style={{ ...S.th, textAlign: "right" }}>현찰 사실 때</th>
                    <th style={{ ...S.th, textAlign: "right" }}>현찰 파실 때</th>
                  </tr>
                </thead>
                <tbody>
                  {bankRows.map((r, i) => {
                    const num = (v) =>
                      v == null ? "—" :
                      Number(v).toLocaleString("ko-KR", { maximumFractionDigits: 2 });
                    return (
                      <tr key={`${r.code}-${i}`}>
                        <td style={{ ...S.td, fontWeight: 700 }}>{r.name}</td>
                        <td style={{ ...S.td, textAlign: "right", fontWeight: 700,
                          fontFamily: "ui-monospace,monospace" }}>{num(r.base)}</td>
                        <td style={{ ...S.td, textAlign: "right",
                          fontFamily: "ui-monospace,monospace" }}>{num(r.tts)}</td>
                        <td style={{ ...S.td, textAlign: "right",
                          fontFamily: "ui-monospace,monospace" }}>{num(r.ttb)}</td>
                        <td style={{ ...S.td, textAlign: "right", color: "#6b7280",
                          fontFamily: "ui-monospace,monospace" }}>{num(r.cash_buy)}</td>
                        <td style={{ ...S.td, textAlign: "right", color: "#6b7280",
                          fontFamily: "ui-monospace,monospace" }}>{num(r.cash_sell)}</td>
                      </tr>
                    );
                  })}
                  {bankRows.length === 0 && (
                    <tr><td style={S.td} colSpan={6}>검색 결과가 없습니다.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div style={S.note}>
          {mode === "customs" ? (
            <>
              출처: 관세청 UNIPASS 관세환율정보 — 매주 일요일 적용 개시. 신고 시점의 적용
              주간 환율을 확인하세요. JPY 등 일부 통화는 표기 단위(100단위 등)에 따라 실제
              적용값이 다를 수 있으니 화폐단위명을 함께 확인하세요.
            </>
          ) : (
            <>
              출처: {bank?.source_label || "은행 고시"} — 참고용이며 고시 회차·거래 은행에
              따라 실제 적용 환율은 다를 수 있습니다. 수출대금 정산(전신환 받으실 때)·수입
              송금(보내실 때) 기준으로 참고하세요.
            </>
          )}
        </div>
      </div>
    </ApiPortalLayout>
  );
}
